import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ThemeProvider,
  TextField,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { gql } from 'graphql.macro';
import React, { FunctionComponent, useState } from 'react';
import { Alert } from '@material-ui/lab';
import { loaderTheme } from '../../../support/Utils';
import LoadingConfig from '../components/LoadingConfig';

const GET_COMPRADORAS = gql`
  query compradoras {
    compradoras {
      id_usuario
      login
      compradora_plm
      compradora
    }
  }
`;

const GET_COMPRADORA_OPTIONS = gql`
  query usuariosParaCompradora {
    usuariosParaCompradora {
      login
      email
      id_usuario
    }
  }
`;

const headerClass = 'caption bold neutral500 uppercase';

const ATUALIZAR_COMPRADORA = gql`
  mutation atualizarCompradora($id_usuario: Int!, $compradora: String!) {
    atualizarCompradora(id_usuario: $id_usuario, compradora: $compradora) {
      id_usuario
      login
      compradora
    }
  }
`;


function getFriendlyErrorMessage(error: any): string {
  const msg = error?.message || '';
  if (msg.includes('Apenas administradores')) {
    return 'Apenas administradores podem atualizar a compradora.';
  }
  if (msg.includes('Usuário não encontrado')) {
    return 'Usuário não encontrado. Verifique o login e tente novamente.';
  }
  if (msg.includes('Network')) {
    return 'Erro de conexão. Verifique sua internet ou tente novamente mais tarde.';
  }
  return 'Ocorreu um erro ao salvar. Tente novamente.';
}

type CompradoraRow = {
  id_usuario: number;
  login: string;
  compradora: string;
  compradora_plm: string;
};

const CompradoraRowComponent: FunctionComponent<{
  row: CompradoraRow;
  onSave: (id_usuario: number, compradora: string) => Promise<void>;
}> = ({ row, onSave }) => {
  const [editValue, setEditValue] = useState(row.compradora_plm);

  React.useEffect(() => {
    setEditValue(row.compradora_plm);
  }, [row.compradora_plm]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { data: optionsData, loading: loadingOptions } = useQuery<{
    usuariosParaCompradora: { login: string; email: string; id_usuario: string }[];
  }>(GET_COMPRADORA_OPTIONS);
  const options = optionsData?.usuariosParaCompradora || [];
  
  const handleSave = async () => {
    setError(null);
    setSuccess(false);
    if (!editValue || editValue.trim() === '') {
      setError('O campo "compradora" é obrigatório e não pode estar vazio.');
      return;
    }
    setSaving(true);
    try {
      await onSave(row.id_usuario, editValue);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      setError(getFriendlyErrorMessage(err));
    }
    setSaving(false);
  };

  return (
    <TableRow>
      <TableCell>{row.compradora}</TableCell>
      <TableCell>
        <Autocomplete
          options={options}
          value={options.find(opt => opt.login === editValue) || null}
          onChange={(_, newValue) => setEditValue(newValue ? newValue.login : '')}
          getOptionSelected={(option, value) => option.login === value.login}
          getOptionLabel={option => option.login}
          fullWidth
          renderInput={params => (
            <TextField {...params} variant="outlined" size="small" fullWidth />
          )}
          disabled={loadingOptions}
        />
      </TableCell>
      <TableCell>
        <Button
          variant='contained'
          className='backgroundPrimary500'
          onClick={handleSave}
          disabled={saving || !editValue || editValue.trim() === ''}
          fullWidth
        >
          {saving ? (
            <ThemeProvider theme={loaderTheme}>
              <CircularProgress size={25} color='primary' />
            </ThemeProvider>
          ) : (
            <span className='text-transform-initial body2 bold primary100'>Salvar</span>
          )}
        </Button>
        {error && <Alert severity="error" style={{ marginTop: 8 }}>{error}</Alert>}
        {success && <Alert severity="success" style={{ marginTop: 8 }}>Salvo!</Alert>}
      </TableCell>
    </TableRow>
  );
};

const CompradoraPCP: FunctionComponent = () => {
  const { loading, data, refetch } = useQuery<{ compradoras: CompradoraRow[] }>(GET_COMPRADORAS);
  const [atualizarCompradora] = useMutation(ATUALIZAR_COMPRADORA);

  const handleSave = async (id_usuario: number, compradora: string) => {
    await atualizarCompradora({
      variables: { id_usuario: parseInt(id_usuario.toString(), 10), compradora },
    });
    await refetch();
  };

  return (
    <div style={{ width: '50%' }}>
      {loading ? (
        <LoadingConfig />
      ) : (
        <div>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow className='tableHeader'>
                <TableCell className={headerClass}>Compradora (Linx)</TableCell>
                <TableCell className={headerClass}>Compradora (PLM)</TableCell>
                <TableCell className={headerClass}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody className='backgroundNeutral50'>
              {data?.compradoras.map((item) => (
                <CompradoraRowComponent
                  key={`compradora-${item.id_usuario}`}
                  row={item}
                  onSave={handleSave}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default CompradoraPCP;